import type { IShopper } from '@makemydeal/dr-platform-types';
import type { CustomerFormValues } from '@makemydeal/dr-activities-customer-card';
import type { SCICommonConsumer } from '@makemydeal/dr-sales-checkin-types';
import { CustomerCardState } from '@makemydeal/dr-sales-checkin-types';

/**
 * Field-name bridge between SCI's stored customer and the Deal Central customer card.
 *
 * SCI stores the customer as a `CommonConsumer`/`SCICommonConsumer` (`cellPhone`, `primaryEmail`,
 * `postalCode`, `street1`, `stateOrProvinceAbbreviation`…). The card (`@makemydeal/dr-activities-customer-card`)
 * reads/writes an `IShopper`-shaped flat form (`phone`, `email`, `zip`, `streetAddress`, `state`…).
 * These two shapes use different field names, so we translate between them here rather than editing
 * the activity (which stays a black box) or leaking the mapping into components/middleware.
 *
 * Kept store-free (pure functions over plain objects) so it can be shared by the app bootstrap
 * middleware (seeding) and the Deal Setup components (reconcile) without a store dependency.
 */

/**
 * Map the card's saved buyer (`IShopper`-shaped form values) back into an `SCICommonConsumer` partial.
 *
 * Returns only the fields the card owns, so the caller can merge it into the existing `checkedInCustomer`
 * (preserving `consumerId`, `dealershipId`, etc.) — keeping SCI state fresh after the card saves.
 */
export const mapShopperToConsumer = (
    input?: CustomerFormValues | Partial<IShopper> | null,
    enableCorrectPhoneHandling = false
): Partial<SCICommonConsumer> => {
    // The card's saved buyer is a flat `CustomerFormValues` whose fields line up with `IShopper`
    // (only `business.legalName` is looser); read it through the `IShopper` shape for mapping.
    const shopper = input as Partial<IShopper> | null | undefined;
    if (!shopper) return {};

    return {
        firstName: shopper.firstName,
        middleName: shopper.middleName,
        lastName: shopper.lastName,
        title: shopper.title,
        suffix: shopper.suffix,
        maritalStatus: shopper.maritalStatus,
        primaryEmail: shopper.email,
        alternateEmail: shopper.emailAlternative,
        cellPhone: enableCorrectPhoneHandling ? shopper.cellPhone : shopper.phone ?? shopper.cellPhone,
        homePhone: shopper.homePhone,
        workPhone: shopper.workPhone,
        street1: shopper.streetAddress,
        street2: shopper.streetAddress2,
        city: shopper.city,
        stateOrProvince: shopper.state,
        stateOrProvinceAbbreviation: shopper.state,
        countyOrRegion: shopper.county,
        country: shopper.country,
        postalCode: shopper.zip,
        customerType: shopper.customerType,
        // Mirror composeConsumer: business legal name lives both top-level (read by buildCreateDealPayload)
        legalName: shopper.business?.legalName,
        business: shopper.business,
        billingAddress: shopper.billingAddress,
        ...(shopper.commonConsumerId && { consumerId: shopper.commonConsumerId }),
        ...(shopper.creditPullConsentGiven !== undefined && {
            creditPullConsentGiven: shopper.creditPullConsentGiven
        })
    };
};

/**
 * Resolves the slideout header title based on the current card state.
 * Co-Buyer search/edit flows show "Co-Buyer"; everything else (including
 * Details and Inbox) shows "Customer Details".
 */
export const getSlideoutTitle = (isCoBuyer: boolean | undefined, state: CustomerCardState): string =>
    isCoBuyer && state !== CustomerCardState.DETAILS && state !== CustomerCardState.INBOX ? 'Co-Buyer' : 'Customer Details';
